import { IndexedDbStorageEngine } from '../engines/indexeddb-storage.engine';
import { LocalStorageEngine } from '../engines/local-storage.engine';
import { SessionStorageEngine } from '../engines/session-storage.engine';
import { StorageEngine } from '../types/storage-engine.enum';
import type {
	AsyncStorageEngine,
	SyncStorageEngine
} from '../types/storage-engine.interface';
import type {
	StorageApi,
	StorageEngineInstance,
	StorageEngineRegistry
} from '../types/storage-types';

export class StorageController implements StorageApi {
	private engine: StorageEngine;

	private readonly engines: StorageEngineRegistry = {
		[StorageEngine.Local]: new LocalStorageEngine(),
		[StorageEngine.Session]: new SessionStorageEngine(),
		[StorageEngine.IndexedDb]: new IndexedDbStorageEngine()
	};

	constructor(engine: StorageEngine = StorageEngine.Local) {
		this.engine = engine;
	}

	public setEngine(engine: StorageEngine): void {
		this.engine = engine;
	}

	public clear() {
		return this.resolveSyncEngine()?.clear();
	}

	public clearExcept(keys: string[]): void {
		this.resolveSyncEngine()?.clearExcept(keys);
	}

	public get<T = unknown>(key: string): T | null {
		const engine = this.resolveSyncEngine();
		return engine?.get<T>(key) ?? null;
	}

	public set<T = unknown>(key: string, value: T): void {
		this.resolveSyncEngine()?.set<T>(key, value);
	}

	public remove(key: string): void {
		this.resolveSyncEngine()?.remove(key);
	}

	public getAsync<T = unknown>(key: string): Promise<T | null> {
		const engine = this.resolveAsyncEngine();
		return engine ? engine.getAsync<T>(key) : Promise.resolve(null);
	}

	public async setAsync<T = unknown>(key: string, value: T): Promise<void> {
		const engine = this.resolveAsyncEngine();

		if (!engine) {
			return;
		}

		await engine.setAsync<T>(key, value);
	}

	public async removeAsync(key: string): Promise<void> {
		const engine = this.resolveAsyncEngine();

		if (!engine) {
			return;
		}

		await engine.removeAsync(key);
	}

	private resolveEngine(): StorageEngineInstance {
		return this.engines[this.engine];
	}

	private resolveSyncEngine(): SyncStorageEngine | null {
		const engine = this.resolveEngine();
		return this.isSyncStorageEngine(engine) ? engine : null;
	}

	private resolveAsyncEngine(): AsyncStorageEngine | null {
		const engine = this.resolveEngine();
		return this.isAsyncStorageEngine(engine) ? engine : null;
	}

	private isSyncStorageEngine(
		engine: StorageEngineInstance
	): engine is SyncStorageEngine {
		return 'get' in engine && 'set' in engine && 'remove' in engine;
	}

	private isAsyncStorageEngine(
		engine: StorageEngineInstance
	): engine is AsyncStorageEngine {
		return (
			'getAsync' in engine && 'setAsync' in engine && 'removeAsync' in engine
		);
	}
}
